import { colors, sxPresets } from '@/theme'
import { ArrowForward } from '@mui/icons-material'
import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import { format } from 'date-fns'
import CollapsibleCard from './CollapsibleCard'
import InvestigationStatusBadge from './InvestigationStatusBadge'

function formatCardDate(date: string): string {
  try {
    return format(new Date(date), 'dd/MM/yyyy')
  } catch {
    return date
  }
}

export interface InvestigationItem {
  id: number | string
  name: string
  statusRaw: string
  statusLabel: string
  createdAt: string
  updatedAt: string
  type?: string
}

interface InvestigationsListProps {
  investigations: InvestigationItem[]
  isLoading?: boolean
  sectionId?: string
  title?: string
  onItemClick?: (id: number | string) => void
}

export default function InvestigationsList({
  investigations,
  isLoading = false,
  sectionId = 'investigations',
  title = 'Investigations',
  onItemClick
}: InvestigationsListProps) {
  const isEmpty = !isLoading && (!investigations || investigations.length === 0)

  return (
    <CollapsibleCard sectionId={sectionId} title={title} isEmpty={isEmpty} emptyMessage='No investigations found'>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box
          sx={{
            ...sxPresets().section.content,
            bgcolor: colors.background.surface,
            ...sxPresets().grid.twoColumn,
            ...sxPresets().scrollable.twoRows
          }}
        >
          {investigations?.map(investigation => (
            <Paper
              key={investigation.id}
              onClick={() => onItemClick?.(investigation.id)}
              elevation={0}
              sx={{
                ...sxPresets().card.default,
                ...(onItemClick ? sxPresets().card.clickable : {}),
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Card Header */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1.5 }}
              >
                <Typography variant='smallText'>
                  {investigation.createdAt ? `Created on: ${formatCardDate(investigation.createdAt)}` : 'Investigation'}
                </Typography>
                {investigation.statusRaw && <InvestigationStatusBadge status={investigation.statusRaw} />}
              </Box>

              {/* Card Body */}
              <Box sx={{ px: 2, pb: 2, flex: 1 }}>
                <Typography variant='cardTitle' sx={{ mb: 1 }}>
                  {investigation.name}
                </Typography>
                {investigation.updatedAt && (
                  <Typography variant='smallText'>Modified on: {formatCardDate(investigation.updatedAt)}</Typography>
                )}
              </Box>

              {/* Card Footer */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                  py: 1.5,
                  bgcolor: colors.brand.primaryLight
                }}
              >
                <Typography variant='link' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  View investigation
                  <ArrowForward sx={{ fontSize: '14px' }} />
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </CollapsibleCard>
  )
}
